import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import i18n from '@core/i18n/config';
import { setAuthTokenWithReadback, generateFlowId } from '@core/api';
import { getUserInfo } from '@core/auth/authApi';
import { isTrustedReturnUrl } from '@core/config/authDomain';
import { captureAuthError } from '@core/sentry/utils';

interface RedirectWithTokenOptions {
  /**
   * JWT from the upstream OAuth / passwordless flow. Typed optional because
   * in-app WebView paths can reach here with the backend response missing
   * `token`. The caller must treat that as a failed login, not a crash.
   */
  token?: string;
  email: string;
  restaurant: string;
  returnUrl: string;
  successMessage: string;
  initialUserId?: string;
}

export async function redirectWithToken({
  token,
  email,
  restaurant,
  returnUrl,
  successMessage,
  initialUserId = '',
}: RedirectWithTokenOptions): Promise<void> {
  const flowId = generateFlowId();

  if (!token) {
    // Defense in depth — the OAuth hook callers already guard on
    // `result.token`, but a future caller wiring a new provider could
    // forget. This branch keeps the Sentry signal distinct from a
    // storage-write failure so we can tell the two apart.
    captureAuthError('auth.redirect_missing_token', {
      flowId,
      restaurant,
      hasEmail: !!email,
      hasReturnUrl: !!returnUrl,
    });
    try {
      await i18n.loadNamespaces('errors');
    } catch { /* fall through to fallback strings */ }
    const title = i18n.t('errors:AUTH_STORAGE_WRITE_FAILED_TITLE', {
      defaultValue: "We couldn't sign you in",
    });
    const description = i18n.t('errors:AUTH_STORAGE_WRITE_FAILED_MESSAGE', {
      defaultValue: 'Please try again or use a different sign-in method.',
    });
    toast.error(title, { description });
    return;
  }

  const tokenWriteReadback = setAuthTokenWithReadback(token, { flowId });
  if (!tokenWriteReadback) {
    // Storage write failed. captureAuthError already recorded it. Surface
    // to the user instead of continuing with a token that's only present in
    // the redirect URL. See setAuthTokenWithReadback docstring.
    Sentry.addBreadcrumb({
      category: 'auth-pre-redirect',
      message: 'Aborting redirect — storage write failed',
      level: 'warning',
      data: { flowId, restaurant, tokenWriteReadback: false },
    });
    // Ensure the errors namespace is loaded before calling t(), otherwise
    // it returns the raw key. If loading fails (network), fall back to a
    // plain-English string so the user never sees the translation key.
    try {
      await i18n.loadNamespaces('errors');
    } catch { /* fall through to fallback strings */ }
    const title = i18n.t('errors:AUTH_STORAGE_WRITE_FAILED_TITLE', {
      defaultValue: "We couldn't sign you in",
    });
    const description = i18n.t('errors:AUTH_STORAGE_WRITE_FAILED_MESSAGE', {
      defaultValue:
        "Your browser wouldn't let us save your session. This usually happens in private browsing mode or when storage is full. Try a regular window or free up some space and try again.",
    });
    toast.error(title, { description });
    // Telemetry: record that the user actually saw an error toast. Pairs
    // with the `auth.token_write_readback_mismatch` event so we can count
    // how many users hit this surface vs how many silently aborted.
    Sentry.addBreadcrumb({
      category: 'user-error-shown',
      message: 'AUTH_STORAGE_WRITE_FAILED',
      level: 'warning',
      data: { flowId, restaurant, surface: 'toast', trigger: 'redirect_with_token' },
    });
    return;
  }

  let userId = initialUserId;
  let preRedirectGetUserInfoStatus: number | string = 'skipped';
  try {
    const info = await getUserInfo(restaurant);
    userId = info.user.user?.id ? String(info.user.user.id) : initialUserId;
    preRedirectGetUserInfoStatus = 200;
  } catch (err) {
    preRedirectGetUserInfoStatus = (err as { status?: number }).status ?? 'error';
  }

  Sentry.addBreadcrumb({
    category: 'auth-pre-redirect',
    message: 'Pre-redirect validation',
    level: 'info',
    data: {
      flowId,
      restaurant,
      tokenWriteReadback,
      preRedirectGetUserInfoStatus,
    },
  });

  if (!returnUrl) {
    toast.success(successMessage);
    return;
  }

  if (!isTrustedReturnUrl(returnUrl)) {
    captureAuthError('auth.untrusted_return_url', {
      flowId,
      restaurant,
      returnUrl,
    });
    toast.success(successMessage);
    return;
  }

  const url = new URL(returnUrl);
  url.searchParams.set('authToken', token);
  url.searchParams.set('afid', flowId);
  if (userId) url.searchParams.set('user_id', userId);
  if (email) url.searchParams.set('email', email);
  window.location.href = url.toString();
}
